import { computed, inject, Injectable, signal } from '@angular/core';

import { DomainData, domainData, IDomainData } from 'src/app/features/domain/models/domain-data';
import { domainProfile, DomainProfile, IDomainProfile } from './models/domain-profile-data';
import { ConfigService } from '../config-services/config.service';
import { UserStorageService } from 'src/app/features/shared/storage/services/storage-services/user-storage-service';
import { AppStorageService } from 'src/app/features/shared/storage/services/storage-services/app-storage-service';
import { AppService } from '../app-services/app.service';
import { joinPath } from '../../functions/join-path.function';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { DomainDefaultProfileApiService } from './domain-default-profile-api.service';
import { imageDto } from 'src/assets/common/models/image-dto.g';
import { ILogosDto, logosDto } from 'src/assets/common/models/logos-dto.g';

@Injectable({
    providedIn: 'root',
})
export class DomainProfileService
{
    private readonly __configService = injectRequired(ConfigService);
    private readonly __userStorageService = injectRequired(UserStorageService);
    private readonly __appStorageService = injectRequired(AppStorageService);
    private readonly __appService = injectRequired(AppService);
    private readonly __defaultProfileApiService = injectRequired(DomainDefaultProfileApiService);

    profileData$$ = computed(() => {
        const __domainProfile = this.__userStorageService.domainProfileData$$?.();
        return __domainProfile
            ?? this.__appStorageService.domainProfileData$$?.()
            ?? this.__appStorageService.defaultDomainProfileData$$?.()
            ?? this.__appService.domainProfile$$();
    });

    // De rauwe shard + de configService-bestandsnamen zijn zelf al signals, dus élke logo-URL kan
    // rechtstreeks als computed erop leunen — net als title$$/title_sub$$ hieronder. Bewust NIET
    // de folder-URL zelf in het profiel bakken (dat was hier ooit een one-time snapshot in
    // DomainProfile's constructor — zie de toelichting bij domainProfile.logos_fileShardPath):
    // deze computed leest ConfigService's signals live, dus een config-load die ná de profieldata
    // binnenkomt (lege cache, twee onafhankelijke netwerkfetches) wordt gewoon nog meegenomen i.p.v.
    // permanent een lege URL te bevriezen.
    private readonly __logosFolderPath$$ = computed(() => this.__buildFolderPath(this.profileData$$()?.[domainProfile.logos_fileShardPath] ?? null));

    // Uitsluitend het daadwerkelijk vastgestelde huidige domein-profiel-object — bewust ZONDER
    // de laatste twee schakels van profileData$$ (defaultDomainProfileData$$/appService-
    // placeholder, de app-identiteit). `null` hier betekent dus: er is momenteel geen domein
    // in beeld (bv. Home/login/vóór de eerste response met domainUserDto) — NIET "domein zonder
    // eigen logo" (dat geeft wél een object terug, met logos_folderPath === null erin, zie
    // __currentDomainLogosFolderPath$$). Dat onderscheid is precies wat logoThumb_url$$ nodig
    // heeft: binnen een domein zonder eigen logo moet de header leeg blijven (geen app-fallback,
    // dat oogt als een echt domain-logo en is verwarrend), maar buiten domein-context (Home)
    // mag de header gewoon het app-logo tonen zoals voorheen.
    private readonly __currentDomainProfile$$ = computed(() =>
        this.__userStorageService.domainProfileData$$?.() ?? this.__appStorageService.domainProfileData$$?.() ?? null);

    private readonly __currentDomainLogosFolderPath$$ = computed(() => this.__buildFolderPath(this.__currentDomainProfile$$()?.[domainProfile.logos_fileShardPath] ?? null));

    // Uitsluitend domain-0 (defaultDomainProfileData$$, de SSOT via /api/Domain/default) — bewust
    // NIET via profileData$$: die keten volgt het laatst bezochte domein en blijft sessie-breed
    // "hangen" op dat domein totdat er weer een response zonder domain-context binnenkomt. Voor
    // plekken die altijd de app-identiteit horen te tonen (bv. het Admin Panel-icoon in de
    // navigatiegeschiedenis) is dat fout: die mogen nooit het laatst bezochte domein overnemen.
    private readonly __appProfile$$ = computed(() => this.__appStorageService.defaultDomainProfileData$$?.() ?? null);
    private readonly __appLogosFolderPath$$ = computed(() => this.__buildFolderPath(this.__appProfile$$()?.[domainProfile.logos_fileShardPath] ?? null));

    // Header-logo: uitsluitend het eigen logo van het huidige domein, geen app-fallback zodra er
    // wél een domein in beeld is. Buiten domein-context (__currentDomainProfile$$ === null, bv.
    // Home) valt hij terug op het app-logo, zoals voorheen. De versie komt rechtstreeks uit de
    // (persistente) profieldata — modifiedOn van de huidige variant, of `null` als de variant
    // niet bestaat — in plaats van een sessie-gebonden bump-signaal: dat laatste start bij elke
    // fresh page load (F5) weer op `null`, waardoor de EERSTE render de kale, ongeversioneerde
    // URL zou opvragen — precies de URL die dit domein al zijn hele geschiedenis gebruikt (vaste
    // publieke bestandsnaam, zie LogoService._PublicFileName) en dus een geliefd doelwit voor
    // jarenoude browsercache (geen Cache-Control op deze publieke bestanden → heuristische cache
    // op Last-Modified). Met de versie uit de echte profieldata is dat vanaf de eerste render al
    // correct, zonder dat er eerst een lokale upload/delete-actie voor nodig is.
    logoThumb_url$$ = computed(() => {
        const __inDomain = !!this.__currentDomainProfile$$();
        const __folderPath = __inDomain ? this.__currentDomainLogosFolderPath$$() : this.__appLogosFolderPath$$();
        const __version = __inDomain ? this.__currentDomainProfile$$()?.[domainProfile.logoThumb_version] ?? null : this.__appProfile$$()?.[domainProfile.logoThumb_version] ?? null;
        return this.__withVersion(this.__buildUrl(this.__configService.logoThumb_fileName$$(), __folderPath), __version);
    });

    // Login/register-logo: leunt bewust op de volle profileData$$-keten (met app-fallback), want
    // die schermen hebben per definitie geen domein-context.
    logo_url$$ = computed(() => this.__withVersion(this.__buildUrl(this.__configService.logo_fileName$$()), this.profileData$$()?.[domainProfile.logo_version] ?? null));

    logoEmail_url$$ = computed(() => this.__withVersion(this.__buildUrl(this.__configService.logoEmail_fileName$$()), this.profileData$$()?.[domainProfile.logoEmail_version] ?? null));

    // Favicon (browsertab-icoon) mag NOOIT leeg zijn: eigen domain-icoon indien aanwezig, anders
    // altijd het app-icoon — óók binnen een domein zonder eigen icoon. Bewust op de per-variant
    // versie varen (null = bestaat niet) i.p.v. op "folderPath niet-null" (zoals logoThumb_url$$
    // hierboven doet): de folderPath is altijd niet-null zodra er een domein in beeld is
    // (guid-gekeyed), dus een simpele `??` op de gebouwde URL zou hier nooit terugvallen op het
    // app-icoon binnen een domein.
    private readonly __currentDomainIconPngVersion$$ = computed(() => this.__currentDomainProfile$$()?.[domainProfile.logoIconPng_version] ?? null);
    private readonly __currentDomainIconSvgVersion$$ = computed(() => this.__currentDomainProfile$$()?.[domainProfile.logoIconSvg_version] ?? null);
    private readonly __appLogoIconPngVersion$$ = computed(() => this.__appProfile$$()?.[domainProfile.logoIconPng_version] ?? null);
    private readonly __appLogoIconSvgVersion$$ = computed(() => this.__appProfile$$()?.[domainProfile.logoIconSvg_version] ?? null);

    private readonly __domainLogoIconPng_url$$ = computed(() => this.__withVersion(this.__buildUrl(this.__configService.logoIconPng_fileName$$(), this.__currentDomainLogosFolderPath$$()), this.__currentDomainIconPngVersion$$()));
    private readonly __domainLogoIconSvg_url$$ = computed(() => this.__withVersion(this.__buildUrl(this.__configService.logoIconSvg_fileName$$(), this.__currentDomainLogosFolderPath$$()), this.__currentDomainIconSvgVersion$$()));
    private readonly __appLogoIconPng_url$$    = computed(() => this.__withVersion(this.__buildUrl(this.__configService.logoIconPng_fileName$$(), this.__appLogosFolderPath$$()), this.__appLogoIconPngVersion$$()));
    private readonly __appLogoIconSvg_url$$    = computed(() => this.__withVersion(this.__buildUrl(this.__configService.logoIconSvg_fileName$$(), this.__appLogosFolderPath$$()), this.__appLogoIconSvgVersion$$()));

    logoIconPng_url$$ = computed(() => this.__currentDomainIconPngVersion$$() ? this.__domainLogoIconPng_url$$() : this.__appLogoIconPng_url$$());
    logoIconSvg_url$$ = computed(() => this.__currentDomainIconSvgVersion$$() ? this.__domainLogoIconSvg_url$$() : this.__appLogoIconSvg_url$$());

    logoAppThumb_url$$ = computed(() => this.__withVersion(
        this.__buildUrl(this.__configService.logoThumb_fileName$$(), this.__appLogosFolderPath$$()),
        this.__appProfile$$()?.[domainProfile.logoThumb_version] ?? null,
    ));

    // Logo-URL van een willekeurig (niet per se "het huidige") domein, rechtstreeks uit diens
    // folderShard — bewust NIET via profileData$$/__logosFolderPath$$, want die volgen alleen
    // het laatst bezochte domein en kunnen dus maar één domein tegelijk resolven. Logo's zijn
    // kale publieke statische bestanden (geen auth), dus de folderShard is het enige
    // domein-specifieke ingrediënt dat nodig is — zie navigation-history.service.ts (meerdere
    // domains tegelijk in de boom, elk met hun eigen logo).
    domainLogoThumb_url(folderShard: string | null, version: string | null): string | null {
        const __rootPublic = this.__configService.rootPublic$$();
        const __logosFolderName = this.__configService.logos_folderName$$();
        const __pathSeperator = this.__configService.pathSeperator$$();
        const __folderPath = __rootPublic && __logosFolderName && folderShard && __pathSeperator
            ? joinPath([__rootPublic, __logosFolderName, folderShard], __pathSeperator)
            : null;
        return this.__withVersion(this.__buildUrl(this.__configService.logoThumb_fileName$$(), __folderPath), version);
    }

    // Zelfde als domainLogoThumb_url, maar reactief-bewust voor het HUIDIGE domein: de
    // navigatiegeschiedenis (navigation-history.service.ts) snapshot't logoFolderShard/
    // logoVersion één keer per bezoek (bij record(), zie _tab-page-route-component.ts) — een
    // logo-upload/-delete tijdens dat bezoek zelf triggert geen nieuwe record()-aanroep, dus die
    // snapshot blijft stil verouderd staan totdat de pagina opnieuw wordt bezocht. Voor de
    // knoop die overeenkomt met het domein waar je momenteel in zit, de live profieldata
    // gebruiken (dezelfde die logoThumb_url$$ voedt, dus altijd actueel na upload/delete) i.p.v.
    // de meegegeven snapshot; voor alle andere (niet-actieve) domeinen in de boom blijft de
    // snapshot de enige beschikbare bron.
    domainLogoThumb_url_live(domainGuid: string | null, folderShard: string | null, version: string | null): string | null {
        const __current = this.__currentDomainProfile$$();
        if (domainGuid && __current?.[domainProfile.guid] === domainGuid) return this.logoThumb_url$$();
        return this.domainLogoThumb_url(folderShard, version);
    }

    title$$ = computed(() => this.profileData$$()?.[domainProfile.title] ?? null);
    title_sub$$ = computed(() => this.profileData$$()?.[domainProfile.title_sub] ?? null);

    constructor() {
        // Domain-0 (het admin-domain) is de SSOT-fallback voor loginscherm + favicon
        // wanneer er geen user- of app-scoped profiel bekend is. Anoniem endpoint, dus
        // altijd bereikbaar (ook uitgelogd); stil falen bij netwerkfout/backend-down —
        // dan blijft de laatst gecachete waarde (of niets) staan.
        void this.__defaultDomainProfile_load();
    }

    private __buildUrl(fileName: string | null, folderPath: string | null = this.__logosFolderPath$$()): string | null {
        const __pathSeperator = this.__configService.pathSeperator$$();
        return folderPath && fileName && __pathSeperator ? joinPath([folderPath, fileName], __pathSeperator) : null;
    }

    // Rauwe shard + ConfigService's (async, via config.json geladen) rootPublic$$/logos_folderName$$
    // reactief samenvoegen tot de folder-URL — zie de toelichting bij domainProfile.logos_fileShardPath
    // voor waarom dit niet (meer) ooit één keer in DomainProfile zelf gebeurt.
    private __buildFolderPath(shardPath: string | null): string | null {
        const __rootPublic = this.__configService.rootPublic$$();
        const __logosFolderName = this.__configService.logos_folderName$$();
        const __pathSeperator = this.__configService.pathSeperator$$();
        return __rootPublic && __logosFolderName && shardPath && __pathSeperator
            ? joinPath([__rootPublic, __logosFolderName, shardPath], __pathSeperator)
            : null;
    }

    domainProfile_set(domain: IDomainData | null): void {

        // Bij domain=null (bv. Account/Home-responses zonder domain-context) hier geen
        // AppService-placeholder invullen: dat zou userStorage/appStorage permanent niet-null
        // maken, waardoor profileData$$() de ??-keten nooit meer bij defaultDomainProfileData$$
        // (de domain-0-fallback) uitkomt. Gewoon null doorzetten laat de keten daadwerkelijk
        // doorvallen; de AppService-placeholder blijft zelf de laatste schakel in die keten.
        const __domainProfile = DomainProfile.create(domain);
        this.__userStorageService.domainProfileData$$?.set(__domainProfile);
        this.__appStorageService.domainProfileData$$?.set(__domainProfile);
    }

    // Patcht de gecachete profieldata van het huidige domein in-place (i.p.v. een volledige
    // domainProfile_set), voor de logo-versievelden die een upload/delete direct en betrouwbaar
    // wijzigt zonder dat er een nieuwe response met domainUserDto voor nodig is (zie
    // logo_bump/logo_clear/logoEmail_bump/logoEmail_clear). Dit is onderdeel van dezelfde
    // (persistente, localStorage-backed) signal-tree als domainProfile_set — overleeft dus ook
    // een page-reload, in tegenstelling tot een los session-signaal.
    private __currentDomainProfile_patch(patch: Partial<IDomainProfile>): void {
        const __apply = (p: DomainProfile | null): DomainProfile | null => p ? { ...p, ...patch } : p;
        this.__userStorageService.domainProfileData$$?.update?.(__apply);
        this.__appStorageService.domainProfileData$$?.update?.(__apply);
    }

    // Na het bewerken van de domeinnaam/subtitel (DomainEditComponent) verandert de gecachete
    // profieldata niet vanzelf — diezelfde staleness als bij logo_bump/logo_clear: de generieke
    // save-flow (_section-edit-component.ts) update alleen het eigen paginamodel, niet de
    // app-brede profieldata die header (domain_name$$), TitleService (browsertab-titel) en
    // NavigationHistoryService.domainLogoThumb_url_live op leunen. Rechtstreeks patchen houdt
    // dat meteen correct, ook zonder page-reload.
    domainTitle_bump(name: string | null, subTitle: string | null): void {
        this.__currentDomainProfile_patch({
            [domainProfile.title]: name,
            [domainProfile.title_sub]: subTitle,
        });
    }

    // Na een volledige logo-delete (alle varianten + de FileBox zelf, zie LogoService.
    // Logo_Delete_Async) bestaan al deze varianten niet meer — alle versievelden dus naar null,
    // zodat logoThumb_url$$ leeg wordt en logoIconPng/Svg_url$$ terugvallen op het app-icoon.
    logo_clear(): void {
        this.__currentDomainProfile_patch({
            [domainProfile.logo_version]: null,
            [domainProfile.logoThumb_version]: null,
            [domainProfile.logoEmail_version]: null,
            [domainProfile.logoIconPng_version]: null,
            [domainProfile.logoIconSvg_version]: null,
        });
    }

    logoEmail_clear(): void {
        this.__currentDomainProfile_patch({ [domainProfile.logoEmail_version]: null });
    }

    // Na een logo-upload/-restore genereert de backend nieuwe bestanden onder dezelfde vaste
    // publieke naam (zie LogoService._PublicFileName) — de URL-STRING verandert dus niet vanzelf.
    // De DTO bevat wél de actuele, per-variant modifiedOn — of guid als terugval: de globale
    // audit-interceptor zet ModifiedOn alléén bij een Modified-save, dus een variant die in dít
    // verzoek voor het eerst is aangemaakt (bv. de allereerste upload) heeft nog modifiedOn=null
    // terwijl-ie wél bestaat (zie domain-profile-data.ts). Rechtstreeks overnemen in de
    // profieldata houdt logoThumb_url$$/logoIconPng/Svg_url$$ meteen correct, óók na een refresh
    // (dit patcht dezelfde persistente profieldata als domainProfile_set).
    logo_bump(dto: ILogosDto | null | undefined): void {
        const __version = (variant: { modifiedOn?: string | null, guid?: string | null } | null | undefined) =>
            variant?.[imageDto.modifiedOn] ?? variant?.[imageDto.guid] ?? null;
        this.__currentDomainProfile_patch({
            [domainProfile.logo_version]: __version(dto?.[logosDto.logoDto]),
            [domainProfile.logoThumb_version]: __version(dto?.[logosDto.logoThumbDto]),
            [domainProfile.logoIconPng_version]: __version(dto?.[logosDto.logoIconPngDto]),
            [domainProfile.logoIconSvg_version]: __version(dto?.[logosDto.logoIconSvgDto]),
        });
    }

    // LogoEmail wordt niet door logo_bump geraakt: een normale logo-upload/-restore hertekent
    // 'm niet (zie LogoService.Logo_Upload_Async — alleen aangemaakt als hij nog ontbreekt), dus
    // een eigen bump nodig bij een gerichte logoEmail-upload.
    logoEmail_bump(dto: ILogosDto | null | undefined): void {
        const __email = dto?.[logosDto.logoEmailDto];
        this.__currentDomainProfile_patch({
            [domainProfile.logoEmail_version]: __email?.[imageDto.modifiedOn] ?? __email?.[imageDto.guid] ?? Date.now().toString(),
        });
    }

    // Bewust ZONDER url-only terugval: url is hier vrijwel altijd niet-null (folderPath +
    // vaste bestandsnaam, zie __buildUrl), óók als de variant niet bestaat — dus zonder
    // version zou dit alsnog de kale, ongeversioneerde URL opleveren. Dat is precies de URL
    // die dit domein al zijn hele geschiedenis gebruikt (vaste publieke bestandsnaam) en dus
    // een geliefd doelwit voor jarenoude browsercache. Geen version → geen URL: null betekent
    // hier consequent "deze variant bestaat niet", overal waar __withVersion gebruikt wordt.
    private __withVersion(url: string | null, version: string | null): string | null {
        return url && version ? `${url}?v=${version}` : null;
    }

    private async __defaultDomainProfile_load(): Promise<void> {
        try {
            const __dto = await this.__defaultProfileApiService.Default_get();
            if (!__dto) return;

            const __domainShapedData = {
                [domainData.logos_fileShardPath]: __dto.logosDto?.folderShard ?? null,
                [domainData.name]: __dto.name,
                [domainData.subTitle]: __dto.appSubTitle ?? null,
                // modifiedOn + guid als terugval (zie domain-profile-data.ts): een variant die
                // nog nooit vervangen is, heeft modifiedOn=null (audit-interceptor zet dat
                // alléén bij een Modified-save) terwijl-ie wél bestaat.
                [domainData.logo_modifiedOn]: __dto.logosDto?.logoDto?.modifiedOn ?? null,
                [domainData.logo_guid]: __dto.logosDto?.logoDto?.guid ?? null,
                [domainData.logoThumb_modifiedOn]: __dto.logosDto?.logoThumbDto?.modifiedOn ?? null,
                [domainData.logoThumb_guid]: __dto.logosDto?.logoThumbDto?.guid ?? null,
                [domainData.logoEmail_modifiedOn]: __dto.logosDto?.logoEmailDto?.modifiedOn ?? null,
                [domainData.logoEmail_guid]: __dto.logosDto?.logoEmailDto?.guid ?? null,
                [domainData.logoIconPng_modifiedOn]: __dto.logosDto?.logoIconPngDto?.modifiedOn ?? null,
                [domainData.logoIconPng_guid]: __dto.logosDto?.logoIconPngDto?.guid ?? null,
                [domainData.logoIconSvg_modifiedOn]: __dto.logosDto?.logoIconSvgDto?.modifiedOn ?? null,
                [domainData.logoIconSvg_guid]: __dto.logosDto?.logoIconSvgDto?.guid ?? null,
            } as unknown as IDomainData;

            const __domainProfile = DomainProfile.create(__domainShapedData);
            this.__appStorageService.defaultDomainProfileData$$?.set(__domainProfile);
        } catch {
            // Backend onbereikbaar — laatst gecachete waarde (of niets) blijft staan.
        }
    }
}
